import { useEffect, useMemo, useState } from 'react';
import { FiCheck, FiLoader, FiPlus, FiX } from 'react-icons/fi';
import { SearchableSelect } from '../SearchableSelect';
import styles from './CategoriaSubCategoriaModal.module.css';

interface SelectOption {
    value: string;
    label: string;
}

interface SubCategoria {
    id: string;
    codigo: string;
    nome: string;
    categoria: { id: string; nome: string };
}

interface CategoriaSubCategoriaModalProps {
    categorias: SelectOption[];
    tipo: string;
    defaultCategoriaId?: string;
    t: (key: string) => string;
    onConfirm: (result: { subCategoriaId: string; subCategoriaNome: string; categoriaId: string; categoriaNome: string }) => void;
    onCriarCategoria: (nome: string) => Promise<SelectOption>;
    onCriarSubCategoria: (nome: string, categoriaId: string) => Promise<SubCategoria>;
    onBuscarSubCategorias: (categoriaId: string) => Promise<SubCategoria[]>;
    onClose: () => void;
    variant?: 'success' | 'danger';
}

type CriandoTipo = 'categoria' | 'subcategoria' | null;

export const CategoriaSubCategoriaModal = ({
    categorias: categoriasProp,
    defaultCategoriaId,
    t,
    onConfirm,
    onCriarCategoria,
    onCriarSubCategoria,
    onBuscarSubCategorias,
    onClose,
    variant = 'success',
}: CategoriaSubCategoriaModalProps) => {
    const [categorias, setCategorias] = useState<SelectOption[]>(categoriasProp);
    const [categoriaId, setCategoriaId] = useState(defaultCategoriaId ?? '');
    const [subCategorias, setSubCategorias] = useState<SubCategoria[]>([]);
    const [subCategoriaId, setSubCategoriaId] = useState('');
    const [loadingSubs, setLoadingSubs] = useState(false);

    const [criando, setCriando] = useState<CriandoTipo>(null);
    const [nomeNovo, setNomeNovo] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!categoriaId) {
            setSubCategorias([]);
            setSubCategoriaId('');
            return;
        }
        let cancelled = false;
        setLoadingSubs(true);
        setSubCategoriaId('');
        onBuscarSubCategorias(categoriaId)
            .then((subs) => { if (!cancelled) setSubCategorias(subs); })
            .catch(() => { if (!cancelled) setError(t('modal.subcategoria.erroCarregarSubs')); })
            .finally(() => { if (!cancelled) setLoadingSubs(false); });
        return () => { cancelled = true; };
    }, [categoriaId, onBuscarSubCategorias]);

    const subCategoriaOptions = useMemo<SelectOption[]>(
        () => subCategorias.map((s) => ({
            value: s.id,
            label: t(s.nome),
        })),
        [subCategorias, t],
    );

    const handleCriarCategoria = async () => {
        const nome = nomeNovo.trim();
        if (!nome) { setError(t('modal.subcategoria.erroCategoria')); return; }
        setSaving(true);
        setError('');
        try {
            const nova = await onCriarCategoria(nome);
            setCategorias((prev) => [...prev, nova]);
            setCategoriaId(nova.value);
            setCriando(null);
            setNomeNovo('');
        } catch (e) {
            setError(e instanceof Error ? e.message : t('modal.subcategoria.erroCategoria'));
        } finally {
            setSaving(false);
        }
    };

    const handleCriarSubCategoria = async () => {
        const nome = nomeNovo.trim();
        if (!nome) { setError(t('modal.subcategoria.erroSubcategoria')); return; }
        if (!categoriaId) { setError(t('modal.subcategoria.erroSelecao')); return; }
        setSaving(true);
        setError('');
        try {
            const nova = await onCriarSubCategoria(nome, categoriaId);
            setSubCategorias((prev) => [...prev, nova]);
            setSubCategoriaId(nova.id);
            setCriando(null);
            setNomeNovo('');
        } catch (e) {
            setError(e instanceof Error ? e.message : t('modal.subcategoria.erroSubcategoria'));
        } finally {
            setSaving(false);
        }
    };

    const handleConcluir = () => {
        if (!categoriaId || !subCategoriaId) {
            setError(t('modal.subcategoria.erroSelecao'));
            return;
        }
        const categoria = categorias.find((c) => c.value === categoriaId);
        const subCategoria = subCategorias.find((s) => s.id === subCategoriaId);
        if (!categoria || !subCategoria) return;
        onConfirm({
            subCategoriaId,
            subCategoriaNome: subCategoria.nome,
            categoriaId,
            categoriaNome: categoria.label,
        });
        onClose();
    };

    const handleAbrirCriacao = (tipo: CriandoTipo) => {
        setCriando(tipo);
        setNomeNovo('');
        setError('');
    };

    const handleCancelarCriacao = () => {
        setCriando(null);
        setNomeNovo('');
        setError('');
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            if (criando === 'categoria') handleCriarCategoria();
            else if (criando === 'subcategoria') handleCriarSubCategoria();
        }
        if (e.key === 'Escape') {
            if (criando) handleCancelarCriacao();
            else onClose();
        }
    };

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.header}>
                    <h3>{t('modal.subcategoria.titulo')}</h3>
                    <button className={styles.closeBtn} onClick={onClose} type="button">
                        <FiX size={18} />
                    </button>
                </div>

                <div className={styles.body}>
                    <label className={styles.fieldLabel}>{t('modal.subcategoria.categoriaPai')}</label>
                    {criando === 'categoria' ? (
                        <div className={styles.inlineCreate}>
                            <input
                                className={styles.input}
                                value={nomeNovo}
                                onChange={(e) => setNomeNovo(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={t('modal.subcategoria.novaCategoria')}
                                autoFocus
                                maxLength={100}
                            />
                            <button type="button" className={`${styles.confirmInlineBtn}${variant === 'danger' ? ` ${styles.confirmInlineBtnDanger}` : ''}`} onClick={handleCriarCategoria} disabled={saving}>
                                {saving ? <FiLoader size={14} /> : <FiCheck size={14} />}
                            </button>
                            <button type="button" className={styles.cancelInlineBtn} onClick={handleCancelarCriacao} disabled={saving}>
                                <FiX size={14} />
                            </button>
                        </div>
                    ) : (
                        <div className={styles.selectWithAction}>
                            <SearchableSelect
                                options={categorias}
                                value={categoriaId}
                                onChange={(v) => { setCategoriaId(v); setError(''); }}
                                placeholder={t('modal.subcategoria.selecionePesquise')}
                                fullWidth
                            />
                            <button
                                type="button"
                                className={`${styles.addBtn}${variant === 'danger' ? ` ${styles.addBtnDanger}` : ''}`}
                                onClick={() => handleAbrirCriacao('categoria')}
                                title={t('modal.subcategoria.categoriaPai')}
                            >
                                <FiPlus size={16} />
                            </button>
                        </div>
                    )}

                    {categoriaId && criando !== 'categoria' && (
                        <>
                            <label className={styles.fieldLabel}>{t('modal.subcategoria.subcategoria')}</label>
                            {criando === 'subcategoria' ? (
                                <div className={styles.inlineCreate}>
                                    <input
                                        className={styles.input}
                                        value={nomeNovo}
                                        onChange={(e) => setNomeNovo(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        placeholder={t('modal.subcategoria.novaSubcategoria')}
                                        autoFocus
                                        maxLength={100}
                                    />
                                    <button type="button" className={`${styles.confirmInlineBtn}${variant === 'danger' ? ` ${styles.confirmInlineBtnDanger}` : ''}`} onClick={handleCriarSubCategoria} disabled={saving}>
                                        {saving ? <FiLoader size={14} /> : <FiCheck size={14} />}
                                    </button>
                                    <button type="button" className={styles.cancelInlineBtn} onClick={handleCancelarCriacao} disabled={saving}>
                                        <FiX size={14} />
                                    </button>
                                </div>
                            ) : (
                                <div className={styles.selectWithAction}>
                                    <SearchableSelect
                                        options={subCategoriaOptions}
                                        value={subCategoriaId}
                                        onChange={(v) => { setSubCategoriaId(v); setError(''); }}
                                        placeholder={loadingSubs ? t('modal.subcategoria.carregando') : t('modal.subcategoria.selecionePesquise')}
                                        fullWidth
                                        disabled={loadingSubs}
                                    />
                                    <button
                                        type="button"
                                        className={`${styles.addBtn}${variant === 'danger' ? ` ${styles.addBtnDanger}` : ''}`}
                                        onClick={() => handleAbrirCriacao('subcategoria')}
                                        title={t('modal.subcategoria.subcategoria')}
                                    >
                                        <FiPlus size={16} />
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {error && <p className={styles.error}>{error}</p>}
                </div>

                <div className={styles.footer}>
                    <button type="button" className={styles.cancelBtn} onClick={onClose}>
                        {t('common.cancelar')}
                    </button>
                    <button
                        type="button"
                        className={`${styles.confirmBtn}${variant === 'danger' ? ` ${styles.confirmBtnDanger}` : ''}`}
                        onClick={handleConcluir}
                        disabled={!categoriaId || !subCategoriaId || !!criando}
                    >
                        <FiCheck size={16} />
                        {t('modal.subcategoria.concluir')}
                    </button>
                </div>
            </div>
        </div>
    );
};